import type { RichTextValueOptions, RichTextValueReturn } from '@/shared/types/tiptap';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { generateHTML, generateJSON } from '@tiptap/html'; // server/browser-safe

import type { JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

const defaultContent: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }],
};

export const FontSizeStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize.replace(/[^\d.]/g, ''),
        renderHTML: attributes => {
          if (!attributes.fontSize) {
            return {};
          }
          return {
            style: `font-size: ${attributes.fontSize}px`,
          };
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
        },
    };
  },
});

const tiptapExtensions = [
  StarterKit,
  Document,
  Paragraph,
  Text,
  BulletList,
  OrderedList,
  ListItem,
  Bold,
  Italic,
  Underline,
  Strike,
  Heading,
  Blockquote,
  CodeBlock,
  Link,
  Image,
  HorizontalRule,
  HardBreak,
  History,
  TextStyle,
  FontSizeStyle,
];

const generateTipTapHTML = ({ type, content }: { type: string; content: any[] }) => {
  return generateHTML({ type, content }, tiptapExtensions);
};

const generateTipTapJSON = (html: string) => {
  return generateJSON(html, tiptapExtensions);
};

const isValidTipTapDoc = (doc: any): doc is { type: string; content: any[] } =>
  !!doc && doc.type === 'doc' && Array.isArray(doc.content) && doc.content.length > 0;

const generateParagraphTipTapDoc = (doc: any): JSONContent => {
  if (doc?.length > 0) {
    return {
      type: 'doc',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: doc }] }],
    };
  }
  return {
    type: 'doc',
    content: [{ type: 'paragraph' }],
  };
};

const hasTipTapContent = (doc: any): boolean => {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.content)) return false;
  return doc.content.some(
    (node: any) =>
      (typeof node.text === 'string' && node.text.trim().length > 0) ||
      (Array.isArray(node.content) && hasTipTapContent(node)),
  );
};

const extractNodeText = (node: any): string => {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text') {
    return typeof node.text === 'string' ? node.text : '';
  }
  if (node.type === 'hardBreak') {
    return '\n';
  }
  if (!Array.isArray(node.content)) return '';
  return node.content.map(extractNodeText).join('');
};

const extractTipTapTextBlocks = (rawValue: string | JSONContent | null | undefined): string[] => {
  if (!rawValue) return [];

  if (typeof rawValue === 'string') {
    try {
      const parsed = JSON.parse(rawValue);
      if (!isValidTipTapDoc(parsed)) {
        const normalized = rawValue.trim();
        return normalized.length > 0 ? [normalized] : [];
      }
      return parsed.content
        .map(extractNodeText)
        .map(value => value.trim())
        .filter(value => value.length > 0);
    } catch {
      const normalized = rawValue.trim();
      return normalized.length > 0 ? [normalized] : [];
    }
  }

  if (!isValidTipTapDoc(rawValue)) return [];

  return rawValue.content
    .map(extractNodeText)
    .map(value => value.trim())
    .filter(value => value.length > 0);
};

const generateRichTextContent = (
  rawValue: string | JSONContent | null | undefined,
  options: RichTextValueOptions = {},
): RichTextValueReturn => {
  const { defaultValue = defaultContent } = options;

  const parsedContent: JSONContent = (() => {
    if (typeof rawValue === 'string') {
      try {
        return JSON.parse(rawValue);
      } catch {
        return defaultValue;
      }
    }
    return rawValue ?? defaultValue;
  })();

  return { parsedContent };
};

export type ParsedStoredRichTextValue = {
  isTipTapDoc: boolean;
  html: string | null;
  plainText: string;
  doc: JSONContent | null;
};

/** Parse a stored observation that may be TipTap JSON or plain text. */
const parseStoredRichTextValue = (rawValue: string | null | undefined): ParsedStoredRichTextValue => {
  if (!rawValue?.trim()) {
    return { isTipTapDoc: false, html: null, plainText: '', doc: null };
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (isValidTipTapDoc(parsed)) {
      return {
        isTipTapDoc: true,
        html: generateTipTapHTML(parsed),
        plainText: extractTipTapTextBlocks(parsed).join('\n'),
        doc: parsed,
      };
    }
  } catch {
    // Plain text observation.
  }

  return { isTipTapDoc: false, html: null, plainText: rawValue, doc: null };
};

const hasStoredRichTextContent = (rawValue: string | null | undefined): boolean => {
  const parsed = parseStoredRichTextValue(rawValue);
  return parsed.isTipTapDoc ? hasTipTapContent(parsed.doc) : Boolean(parsed.plainText.trim());
};

export {
  extractTipTapTextBlocks,
  generateTipTapHTML,
  isValidTipTapDoc,
  generateTipTapJSON,
  generateRichTextContent,
  generateParagraphTipTapDoc,
  hasTipTapContent,
  parseStoredRichTextValue,
  hasStoredRichTextContent,
};
